import React, { useMemo, useState } from "react";
import AdminPageShell from "./AdminPageShell";
import useFetch from "../../useFetch";
import { apiRequest } from "../../utils/api";

const statuses = [
  { value: "", label: "All Messages" },
  { value: "new", label: "New" },
  { value: "read", label: "Read" },
  { value: "archived", label: "Archived" },
];

const formatDate = (date) => date ? new Date(date).toLocaleString() : "Not available";

const statusClass = (status) => {
  if (status === "archived") return "warning";
  if (status === "new") return "";
  return "success";
};

const AdminContactMessages = () => {
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({ keyword: "", status: "" });
  const [refreshKey, setRefreshKey] = useState(0);
  const [message, setMessage] = useState(false);
  const [actionError, setActionError] = useState(false);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("per_page", "100");
    if (appliedFilters.keyword) params.set("keyword", appliedFilters.keyword);
    if (appliedFilters.status) params.set("status", appliedFilters.status);
    if (refreshKey) params.set("_", refreshKey);

    return process.env.REACT_APP_BASE_API_URL + "/admin/contact-messages?" + params.toString();
  }, [appliedFilters, refreshKey]);

  const { response, isLoading, error } = useFetch(apiUrl);
  const messages = response?.messages?.data ?? [];
  const stats = response?.stats ?? {};
  const total = response?.messages?.meta?.total ?? messages.length;

  const applyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters({ keyword: keyword.trim(), status });
  };

  const clearFilters = () => {
    setKeyword("");
    setStatus("");
    setAppliedFilters({ keyword: "", status: "" });
  };

  const refresh = () => setRefreshKey(Date.now());

  const updateStatus = (item, nextStatus) => {
    setMessage(false);
    setActionError(false);
    const formData = new FormData();
    formData.append("status", nextStatus);

    apiRequest(`/admin/contact-messages/${item.id}/status`, { method: "POST", body: formData })
      .then((data) => {
        setMessage(data.message);
        refresh();
      })
      .catch((error) => setActionError(error.message));
  };

  const removeMessage = (item) => {
    if (!window.confirm("Remove this contact message?")) return;

    setMessage(false);
    setActionError(false);
    apiRequest(`/admin/contact-messages/${item.id}`, { method: "DELETE" })
      .then((data) => {
        setMessage(data.message);
        refresh();
      })
      .catch((error) => setActionError(error.message));
  };

  return (
    <AdminPageShell
      active="messages"
      title="Contact Messages"
      text="Review and manage messages submitted from the public contact form."
    >
      <div className="row">
        {[
          { id: "new", label: "New", value: stats.new ?? 0 },
          { id: "read", label: "Read", value: stats.read ?? 0 },
          { id: "archived", label: "Archived", value: stats.archived ?? 0 },
          { id: "total", label: "Total", value: stats.total ?? 0 },
        ].map((item) => (
          <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12" key={item.id}>
            <div className="ui-item">
              <div className="right">
                <h4>{item.value}</h4>
                <p>{item.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="ls-widget">
        <div className="widget-title">
          <h4>Messages</h4>
        </div>

        <form className="admin-filter-bar" onSubmit={applyFilters}>
          <div className="admin-filter-field">
            <label>Search</label>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Name, email, subject, or message"
            />
          </div>
          <div className="admin-filter-field">
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {statuses.map((item) => (
                <option value={item.value} key={item.value}>{item.label}</option>
              ))}
            </select>
          </div>
          <div className="admin-filter-actions">
            <button type="submit" className="theme-btn btn-style-one">Apply</button>
            <button type="button" className="theme-btn btn-style-three" onClick={clearFilters}>Clear</button>
          </div>
        </form>

        <div className="widget-content">
          {message && <div className="alert alert-success"><strong>{message}</strong></div>}
          {actionError && <div className="alert alert-danger"><strong>{actionError}</strong></div>}
          {error && <div className="alert alert-danger"><strong>{error}</strong></div>}
          {isLoading && <h5 className="text-center my-3">Loading Data</h5>}

          {!error && !isLoading && <div className="admin-list-summary">Showing {messages.length} of {total} messages</div>}

          <div className="admin-table-list">
            {!error && !isLoading && messages.map((item) => (
              <div className="admin-table-row admin-message-row" key={item.id}>
                <div>
                  <strong>{item.subject || "No subject"}</strong>
                  <span>{item.name} - {item.email} - {formatDate(item.created_at)}</span>
                  <p>{item.message}</p>
                </div>
                <div className="admin-row-actions">
                  <span className={statusClass(item.status)}>{item.status}</span>
                  {item.status !== "read" && (
                    <button type="button" className="theme-btn btn-style-three" onClick={() => updateStatus(item, "read")}>Mark Read</button>
                  )}
                  {item.status !== "new" && (
                    <button type="button" className="theme-btn btn-style-three" onClick={() => updateStatus(item, "new")}>Mark New</button>
                  )}
                  {item.status !== "archived" && (
                    <button type="button" className="theme-btn btn-style-three" onClick={() => updateStatus(item, "archived")}>Archive</button>
                  )}
                  <button type="button" className="theme-btn btn-style-danger" onClick={() => removeMessage(item)}>Delete</button>
                </div>
              </div>
            ))}
            {!error && !isLoading && messages.length === 0 && <div className="admin-empty-state">No contact messages found.</div>}
          </div>
        </div>
      </div>
    </AdminPageShell>
  );
};

export default AdminContactMessages;
